import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxSearch,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/Combobox'
import { type ControlSize } from '@/components/ui/control'
import {
  useClients,
  useCreateParticipant,
  useParticipants,
} from '@/features/directories/api/useDirectory'
import {
  ParticipantFormSheet,
  type ParticipantDraft,
} from '@/features/directories/components/DirectoryFormSheets'
import type { ParticipantRole } from '@/types/domain'

/* Who paid us, or whom we paid — picked from the directory rather than typed.
 *
 * ── WHY THIS IS WORTH DOING, AND IT IS NOT VALIDATION ──
 *
 * `payments.counterparty_name` is FREE TEXT on purpose and stays that way. 0035
 * argues it at length: a bank statement arrives with a name printed on it, and
 * requiring a directory record before the money can be recorded would make the
 * directory a precondition for recording that money arrived.
 *
 * What this control buys is SPELLING. `finance/balances.go` keys every
 * counterparty balance by that name — it must, because finance may not read
 * `participants` at all (AD-2/AD-3) — so «Zurdinov Ruslan» and «Zurdinov
 * Ruslan» typed with two spaces are TWO balances today, each half right, and
 * neither of them the figure somebody is chasing. Picking from a list makes them
 * one. That is the whole benefit and it is enough.
 *
 * WHAT IS SUBMITTED IS STILL THE NAME. This control emits a string and never an
 * id, and no counterparty id column exists to write one into. A payment does not
 * point at a directory row; it records what was written on the transfer.
 *
 * ── What is offered: everything, narrowed by nothing ──
 *
 * CLIENTS and PARTICIPANTS together, because a payment's counterparty is the
 * case's client on the way in and a carrier, an agent or a vehicle owner on the
 * way out, and both halves of the book therefore have to be in one list.
 *
 * NOT narrowed by `direction`, and the choice is deliberate rather than lazy.
 * Three reasons, in order of weight:
 *
 *   1. the mapping direction→side-of-the-book is FALSE, and the schema says so
 *      in two places. 0035's `payment_direction` comment: "a refund is an 'out'
 *      payment to the client, never a third direction". `payment.go` on
 *      `DirectionIn`: "money received — from the client, OR A RECOVERY FROM A
 *      COUNTERPARTY". So each direction legitimately reaches both sides, and
 *      narrowing would hide the needed row in exactly the two cases the code
 *      already documents.
 *   2. the direction is a control on this same form. A list that reshuffled when
 *      it changed would take a row that had already been picked out from under
 *      the person who picked it — which is the one thing this control must never
 *      do (see the "as entered" row below).
 *   3. the cost of not narrowing is a long list, and a long list is what the
 *      search box and the type hint are for. The cost of narrowing is a row
 *      somebody cannot find, and their answer to that is to work around the
 *      picker — which loses the spelling this control exists to fix.
 *
 * Nor is it narrowed by participant TYPE, which is where this departs from
 * `CarrierSelect` next door. A carrier is a thing that moves cargo, so five of
 * FR-40's seven types are not carriers. A payer is not a thing at all — it is
 * whoever the money came from, and a consignee settling a debit note is
 * ordinary. The one type filter that matters is already applied SERVER-side:
 * agent-typed rows are absent, not hidden, for a caller who may not read them
 * (FR-29).
 *
 * ── What merely stays selected ──
 *
 * ACTIVE rows are offered; a deactivated counterparty is one the desk decided
 * not to work with again, and offering them would make deactivation cosmetic.
 *
 * But the value already on the record is kept SELECTED whether or not it matches
 * anything, shown as «как введено» — every payment recorded before this control
 * existed, and every name read off a transfer for a company nobody has a record
 * of. NOTHING HERE MAY REJECT DATA THAT IS ALREADY SAVED, which is the same rule
 * `CarrierSelect` follows for a pre-0046 leg and `CountryDropdown` for a port
 * name stored before that field became a picker.
 *
 * ── The add-a-counterparty escape hatch ──
 *
 * Inside the picker rather than beside it, for `CarrierSelect`'s reason: the
 * counterparty standing in front of somebody at 7pm is frequently not in the
 * book yet, and a picker that makes them abandon the payment, cross to the
 * directory screen and start again is a picker whose users go back to typing.
 * The payment sheet stays open holding its draft throughout — the participant
 * form is a second sheet over the first, not a navigation.
 */

/* The three types a payment goes OUT to, offered by the create form. Not all
   seven: the hatch exists for the party somebody is paying right now, and a
   type select of seven roles in front of a person who wants to record a
   transfer is a form, not an escape.

   A CLIENT is deliberately not creatable here either. A client the company is
   taking money from has a case, and a case cannot be registered without one —
   so a client missing from the directory is a different problem from the one
   this button solves, and creating a second record for one that exists is the
   duplicate this whole control fights.

   Deliberately NOT exported: a non-component export from a component file breaks
   fast refresh for the module (react-refresh/only-export-components). */
const PAYABLE_TYPES: ParticipantRole[] = ['counter-agent', 'agent', 'vehicle-owner']

/** The value the "as entered" row carries.
 *
 *  A sentinel rather than the name itself, so `onChange` can tell "the user
 *  re-picked the stored value" from "the user picked a directory row". It cannot
 *  collide, because every other row id here is prefixed with its source. */
const AS_ENTERED = 'as-entered'

export interface CounterpartySelectProps {
  /** The counterparty's NAME as the record carries it. There is no id half:
   *  what the payment stores, and what this control emits, is the name. */
  value: string
  /** Always a name, never an id. Re-picking the stored row emits it unchanged
   *  rather than inventing a link the payment never had. */
  onChange: (name: string) => void
  id?: string
  disabled?: boolean
  size?: ControlSize
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

/* Base UI: none directly — composed from Combobox.tsx, which is the
   `@base-ui/react` combobox primitive wearing this system's tokens. */
export function CounterpartySelect({
  value,
  onChange,
  id,
  disabled,
  size = 'md',
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: CounterpartySelectProps) {
  const { t } = useTranslation()

  /* Two queries, one per directory, both ordinary cached reads — the second
     payment sheet opened in a session issues neither. They are read HERE rather
     than passed in for the reason the payment sheet reads its own cases and
     entities: the server decides which rows this caller may see (AD-5, FR-29),
     and a parent passing a list down would be a second, narrower answer to a
     question the endpoint already answers. */
  const clients = useClients()
  const participants = useParticipants()

  const createParticipant = useCreateParticipant()
  const [adding, setAdding] = useState(false)

  /* The list's own open state, held here rather than left to the primitive, for
     the reason CarrierSelect states: the add button opens a second sheet, both
     are portaled to <body>, the popup mounts last, and no z-index the sheet can
     carry wins against a sibling later in document order — so the participant
     form rendered BEHIND its own trigger's list. Closing it is also right
     independently: the question "which of these?" has been answered with "none
     of them". */
  const [listOpen, setListOpen] = useState(false)

  /* Prefixed ids, so a client and a participant that happen to share a uuid
     cannot collide — and so the sentinel above cannot be mistaken for either.
     The row id is a list key and nothing else; nothing is submitted but `name`. */
  const rows = [
    ...(clients.data ?? [])
      .filter((client) => client.isActive)
      .map((client) => ({
        id: `client:${client.id}`,
        name: client.name,
        hint: t('finance.payments.counterpartyKindClient'),
      })),
    ...(participants.data ?? [])
      .filter((participant) => participant.isActive)
      .map((participant) => ({
        id: `participant:${participant.id}`,
        name: participant.name,
        hint: t(`participantRole.${participant.type}`),
      })),
  ].sort((a, b) => a.name.localeCompare(b.name))

  const saved = value.trim()

  /** A saved name that no offered row spells the same way: recorded before this
   *  control existed, read off a transfer, or belonging to a counterparty since
   *  deactivated. Offered as its own first row and marked "as entered". */
  const asEntered = saved !== '' && !rows.some((row) => row.name === saved)

  const all = asEntered
    ? [
        {
          id: AS_ENTERED,
          name: saved,
          hint: t('finance.payments.counterpartyAsEntered'),
        },
        ...rows,
      ]
    : rows

  const byID = new Map(all.map((row) => [row.id, row]))

  /* What Base UI is holding. The control is keyed by ROW rather than by name so
     two directory records spelled identically stay two rows; the value that
     leaves is the name either way. */
  const selected =
    saved === ''
      ? null
      : (all.find((row) => row.name === saved)?.id ?? (asEntered ? AS_ENTERED : null))

  return (
    <>
      <Combobox
        items={all.map((row) => row.id)}
        value={selected}
        onValueChange={(next: string | null) => {
          // Cleared, or a row that is no longer in the list. Emitting '' rather
          // than keeping the old name: the field is required and an empty box
          // that still submits a name is the mismatch a form must never have.
          onChange(next === null ? '' : (byID.get(next)?.name ?? ''))
        }}
        itemToStringLabel={(rowID: string) => byID.get(rowID)?.name ?? ''}
        /* One keystroke matches the name AND the type, so "перевозчик" finds the
           hauliers when somebody is looking for a kind of company rather than
           for one they can already name. The default filter sees only the
           label. */
        filter={(rowID: string, query: string) => {
          const needle = query.trim().toLowerCase()
          if (needle === '') return true
          const row = byID.get(rowID)
          if (!row) return false
          return `${row.name} ${row.hint}`.toLowerCase().includes(needle)
        }}
        disabled={disabled}
        autoHighlight
        open={listOpen}
        onOpenChange={setListOpen}
      >
        <ComboboxTrigger
          id={id}
          triggerSize={size}
          aria-describedby={describedBy}
          aria-invalid={invalid}
        >
          <ComboboxValue>
            {(rowID: string | null) => {
              /* The stored name is the fallback, and it matters: both queries
                 are still in flight on the first paint of a sheet opened over a
                 saved payment, so `byID` is empty and a label-only trigger would
                 flash the placeholder over a payment that does name its payer. */
              const label = (rowID ? byID.get(rowID)?.name : '') || saved
              if (!label) {
                return (
                  <span className="text-text-faint truncate">
                    {t('finance.payments.counterpartyPlaceholder')}
                  </span>
                )
              }
              return <span className="text-text-body truncate">{label}</span>
            }}
          </ComboboxValue>
        </ComboboxTrigger>

        <ComboboxContent>
          <ComboboxSearch placeholder={t('finance.payments.counterpartySearch')} />

          <ComboboxEmpty hint={t('common.form.noMatchesHint')}>
            {t('common.form.noMatches')}
          </ComboboxEmpty>

          <ComboboxList>
            {(rowID: string) => {
              const row = byID.get(rowID)
              if (!row) return null
              return (
                <ComboboxItem key={rowID} value={rowID}>
                  <span className="min-w-0 flex-1 truncate">{row.name}</span>
                  {/* The type, one step down and muted — the secondary reading
                      of the same row rather than a second label. The same
                      company is frequently both a client and a counter-agent,
                      and the hint is how the reader tells the two rows apart. */}
                  <span className="text-text-muted shrink-0 text-[12px]">{row.hint}</span>
                </ComboboxItem>
              )
            }}
          </ComboboxList>

          {/* Pinned under the list rather than offered as a row in it: it is not
              a counterparty, and a filter that hid it when nothing matched would
              hide it exactly when it is most wanted. A plain button, not a
              ComboboxItem, so keyboard navigation does not land on something
              that is not a choice. */}
          <div className="border-border-subtle -mx-1 -mb-1 mt-1 border-t px-1 pt-1">
            <button
              type="button"
              onClick={() => {
                setListOpen(false)
                setAdding(true)
              }}
              className="text-brand hover:bg-surface-hover flex w-full cursor-pointer items-center gap-1.5 rounded-[5px] px-2 py-1.5 text-left text-[12.5px]"
            >
              <Plus size={14} aria-hidden />
              {t('finance.payments.counterpartyAdd')}
            </button>
          </div>
        </ComboboxContent>
      </Combobox>

      {/* The directory's own editor, over the payment sheet rather than instead
          of it. The payment draft is untouched — this sheet is a sibling in the
          same React tree, and `useFormDraft` re-seeds only when the sheet it
          belongs to closes or changes record. */}
      <ParticipantFormSheet
        open={adding}
        onClose={() => setAdding(false)}
        creatableTypes={PAYABLE_TYPES}
        pending={createParticipant.isPending}
        onSubmit={(draft: ParticipantDraft) =>
          createParticipant.mutate(
            {
              type: draft.type,
              name: draft.name.trim(),
              taxId: draft.taxId.trim(),
              registrationId: draft.registrationId.trim(),
              country: draft.country.trim(),
              address: draft.address.trim(),
              contacts: draft.contacts,
            },
            {
              // Selected on the payment the moment it exists, which is the whole
              // point of the hatch: somebody who has just typed a company's name
              // must not have to find it in a list afterwards.
              onSuccess: (created) => {
                onChange(created.name)
                setAdding(false)
              },
            },
          )
        }
      />
    </>
  )
}
